import { useState } from "react";
import { useLocation } from "react-router-dom";
import { EditBulkClassMutation } from "src/api/Class";
import { BorderButton } from "src/components/buttons/BorderButton";
import InputCurrency from "src/components/inputs/InputCurrency";
import InputRadio from "src/components/inputs/InputRadio";
import { InputRadioGroup } from "src/components/inputs/InputRadioGroup";
import SlideDown from "src/components/layout/SlideDown";
import { apiClient } from "src/stores/Api";
import { useStore } from "src/stores/Store";
import message from "src/utils/message";
import { modals, useModal } from "src/utils/modals";
import { bulkClass, editBulkClassVariables } from "types/code-generator";
import Modal from "./Modal";

interface Props {
  counts: bulkClass["bulkClass"];
  onNetworkAction: () => void;
  itemId: string;
  itemTitle: string;
}

export const BulkEditMembershipPricesModal = modals.create((props: Props) => {
  const modal = useModal();
  const location = useLocation();
  const store = useStore();
  const [enable, setEnable] = useState(null);
  const [price, setPrice] = useState("");

  async function onSubmit() {
    const variables: editBulkClassVariables = {
      data: {
        membershipPrices: {
          membershipOfferingId: props.itemId,
          price: parseInt(price) || 0,
          add: enable === true,
        },
      },
      where: {
        organizationId: store.organization.id,
      },
      filter: location.search,
    };

    const processingMessage = message.loading("Updating");

    await apiClient(EditBulkClassMutation, {
      variables,
    });

    processingMessage();
    message.success("Updated!");

    if (props.onNetworkAction) {
      props.onNetworkAction();
    }

    onCancel();
  }

  function onCancel() {
    modal.hide();
  }

  const enabled = props.counts.membershipPrices[props.itemId]?.length || 0;
  let warning;

  const type = "offering";
  const title = "Membership Pricing";
  const description = `Of the selected ${props.counts.total.toLocaleString()} ${type}${
    props.counts.total !== 1 ? "s" : ""
  }, ${enabled.toLocaleString()} ${
    enabled === 1 ? " offers " : " offer "
  } adjusted price for those enrolled in ${props.itemTitle}.`;
  const enableLabel = "Offer adjusted price";
  const disableLabel = "Do not adjust price";

  const eligible = props.counts.membershipOfferings[props.itemId]?.length || 0;
  const ineligible =
    props.counts.total - eligible - props.counts.allowNonMembers;

  if (ineligible > 0) {
    warning = (
      <div className="flex gap-2">
        <i className="far fa-triangle-exclamation mt-1 text-main" />
        <div className="flex-1">
          An adjustment in price cannot be enabled for{" "}
          {ineligible.toLocaleString()} selected offerings because this
          membership does not provide eligibility for registration.
        </div>
      </div>
    );
  }

  return (
    <Modal
      visible={modal.visible}
      afterClose={modal.remove}
      title={title}
      handleCancel={onCancel}
      footer={
        <>
          <BorderButton type="submit" onClick={onSubmit}>
            Save & Apply
          </BorderButton>
          <BorderButton onClick={onCancel}>Cancel</BorderButton>
        </>
      }
    >
      <p>{description}</p>

      <hr />

      <div>
        <InputRadioGroup onChange={setEnable} value={enable}>
          <InputRadio
            value={true}
            label={`${enableLabel} for all ${props.counts.total.toLocaleString()} ${type}${
              props.counts.total !== 1 ? "s" : ""
            }`}
          >
            <SlideDown>
              {enable && (
                <div>
                  <div className="mb-2">
                    <strong>Set the price</strong>
                  </div>
                  <InputCurrency
                    placeholder="Enter price"
                    value={price}
                    onChange={(value) => setPrice(value.toString())}
                  />
                </div>
              )}
            </SlideDown>
          </InputRadio>
          <InputRadio
            value={false}
            label={`${disableLabel} for all ${props.counts.total.toLocaleString()} ${type}${
              props.counts.total !== 1 ? "s" : ""
            }`}
          />
        </InputRadioGroup>

        {warning && (
          <div className="mb-4">
            <hr />
            {warning}
          </div>
        )}
      </div>
    </Modal>
  );
});
